import { BookingRecord } from "@/app/Bookings/GraphQL/bookingRecordQuery";
import { relocationListQuery } from "@/app/Relocations/GraphQL/relocationListQuery";
import {
  QueryRelocationsWhereColumn,
  QueryRelocationsWhereDeliveryCityColumn,
  QueryRelocationsWhereDepartureCityColumn,
  QueryRelocationsWhereVehicleColumn,
  RelocationListQueryVariables,
  RelocationStatus,
  SqlOperator,
} from "@/gql/graphql";
import dayjs from "dayjs";
import { useSuspenseGqlQuery } from "@/lib/GraphQLCodegen/fetcher";

export function getMatchingRelocationVariables(
  booking: BookingRecord,
): Partial<RelocationListQueryVariables> {
  return {
    status: [RelocationStatus.Ready],
    type: [booking.relocation.type],
    whereDepartureCity: {
      column: QueryRelocationsWhereDepartureCityColumn.Id,
      value: booking.departureOffice.city.id,
    },
    whereDeliveryCity: {
      column: QueryRelocationsWhereDeliveryCityColumn.Id,
      value: booking.deliveryOffice.city.id,
    },
    where: {
      AND: [
        {
          column: QueryRelocationsWhereColumn.Id,
          value: booking.relocation.id,
          operator: SqlOperator.Neq,
        },
        {
          column: QueryRelocationsWhereColumn.AvailableFromDate,
          value: dayjs(booking.depart_at).format("YYYY-MM-DD"),
          operator: SqlOperator.Lte,
        },
        {
          column: QueryRelocationsWhereColumn.LatestDepartureDate,
          value: dayjs(booking.depart_at).format("YYYY-MM-DD"),
          operator: SqlOperator.Gte,
        },
        {
          column: QueryRelocationsWhereColumn.AvailableToDate,
          value: dayjs(booking.deliver_at).format("YYYY-MM-DD"),
          operator: SqlOperator.Gte,
        },
      ],
    },
    whereVehicle: {
      AND: [
        {
          column: QueryRelocationsWhereVehicleColumn.Type,
          value: booking.vehicle.type,
          operator: SqlOperator.Eq,
        },
        {
          column: QueryRelocationsWhereVehicleColumn.Seatbelts,
          value: booking.number_of_travellers,
          operator: SqlOperator.Gte,
        },
        {
          column: QueryRelocationsWhereVehicleColumn.MinimumAge,
          operator: SqlOperator.Lte,
          value: dayjs(booking.depart_at).diff(booking.date_of_birth, "year"),
        },
      ],
    },
  };
}

export function getSimilarRelocationVariables(
  booking: BookingRecord,
  excludedRelocationIds: string[],
): Partial<RelocationListQueryVariables> {
  return {
    status: [RelocationStatus.Ready],
    type: [booking.relocation.type],
    whereDepartureCity: {
      operator: SqlOperator.In,
      column: QueryRelocationsWhereDepartureCityColumn.Id,
      value: [booking.departureOffice.city.id, booking.deliveryOffice.city.id],
    },
    whereDeliveryCity: {
      column: QueryRelocationsWhereDeliveryCityColumn.Id,
      operator: SqlOperator.In,
      value: [booking.departureOffice.city.id, booking.deliveryOffice.city.id],
    },
    where: {
      AND: [
        {
          column: QueryRelocationsWhereColumn.Id,
          value: [booking.relocation.id, ...excludedRelocationIds],
          operator: SqlOperator.NotIn,
        },
        {
          column: QueryRelocationsWhereColumn.AvailableFromDate,
          value: dayjs(booking.depart_at).add(2, "day").format("YYYY-MM-DD"),
          operator: SqlOperator.Lte,
        },
        {
          column: QueryRelocationsWhereColumn.LatestDepartureDate,
          value: dayjs(booking.depart_at)
            .subtract(2, "day")
            .format("YYYY-MM-DD"),
          operator: SqlOperator.Gte,
        },
        {
          column: QueryRelocationsWhereColumn.AvailableToDate,
          value: dayjs(booking.deliver_at)
            .subtract(2, "day")
            .format("YYYY-MM-DD"),
          operator: SqlOperator.Gte,
        },
      ],
    },
    whereVehicle: {
      AND: [
        {
          column: QueryRelocationsWhereVehicleColumn.Seatbelts,
          value: booking.number_of_travellers,
          operator: SqlOperator.Gte,
        },
        {
          column: QueryRelocationsWhereVehicleColumn.MinimumAge,
          operator: SqlOperator.Lte,
          value: dayjs(booking.depart_at).diff(booking.date_of_birth, "year"),
        },
      ],
    },
  };
}

/** The page/sort args both tables' DataTables ask for by default. */
const listVariables = {
  page: 1,
  first: 200,
  orderBy: [],
};

/**
 * Ids the matching table shows — the similar table filters them out so a
 * relocation never appears in both.
 */
export function useMatchingRelocationIds(booking: BookingRecord) {
  const { data } = useSuspenseGqlQuery(relocationListQuery, {
    ...listVariables,
    ...getMatchingRelocationVariables(booking),
  });

  return data.relocations.data.map((relocation) => relocation.id);
}

/**
 * Resolves both tables' data up front so they render populated instead of
 * flashing their own loading states. The similar query can only be built once
 * the matching ids are known, hence the sequential awaits.
 */
export function useFindRelocationTables(booking: BookingRecord) {
  const matchingIds = useMatchingRelocationIds(booking);

  useSuspenseGqlQuery(relocationListQuery, {
    ...listVariables,
    ...getSimilarRelocationVariables(booking, matchingIds),
  });
}
